import { ZERO_IN_BIG_NUMBER } from '@shared/utils/constant'
import { TradeDirection } from '@injectivelabs/sdk-ts/types/light'
import { BigNumber, toBigNumber } from '@injectivelabs/utils/big-number'
import { calculateNotional } from '@/app/utils/trading/calculations'
import type { TcDerivativePosition } from '@injectivelabs/sdk-ts'

export type PostTradePosition = {
  margin: BigNumber
  quantity: BigNumber
  entryPrice: BigNumber
  direction: TradeDirection
}

// mirrors chain logic :
// - same-side adds use a weighted-average entry and sum the margins;
// - opposite-side fills release margin prorated to the closed portion.
// - on a flip the new leg's margin is the order's margin scaled by remainder/orderQty
// - reduce-only passes `order.margin = 0` — release still prorates off the
//   existing position margin, so the reduce branch produces the correct
//   post-trade margin without special-casing.
export function calculatePostTradePosition(params: {
  currentPosition: undefined | TcDerivativePosition
  order: {
    isBuy: boolean
    price: BigNumber
    margin: BigNumber
    quantity: BigNumber
  }
}): PostTradePosition {
  const { currentPosition, order } = params

  const positionQuantity = toBigNumber(currentPosition?.quantity || 0)

  if (!currentPosition || positionQuantity.isZero()) {
    return {
      margin: order.margin,
      quantity: order.quantity,
      direction: order.isBuy ? TradeDirection.Long : TradeDirection.Short,
      entryPrice: order.quantity.isZero() ? ZERO_IN_BIG_NUMBER : order.price
    }
  }

  const positionMargin = toBigNumber(currentPosition.margin)
  const positionEntry = toBigNumber(currentPosition.entryPrice)
  const isPositionLong = currentPosition.direction === TradeDirection.Long
  const isSameSide =
    (order.isBuy && isPositionLong) || (!order.isBuy && !isPositionLong)

  if (isSameSide) {
    const positionNotional = calculateNotional({
      price: positionEntry,
      quantity: positionQuantity
    })
    const orderNotional = calculateNotional({
      price: order.price,
      quantity: order.quantity
    })
    const newQuantity = positionQuantity.plus(order.quantity)
    const newEntryPrice = positionNotional.plus(orderNotional).div(newQuantity)

    return {
      quantity: newQuantity,
      entryPrice: newEntryPrice,
      direction: currentPosition.direction,
      margin: positionMargin.plus(order.margin)
    }
  }

  if (order.quantity.lte(positionQuantity)) {
    const remainingQuantity = positionQuantity.minus(order.quantity)
    const releasedMargin = positionMargin
      .times(order.quantity)
      .div(positionQuantity)
    const remainingMargin = BigNumber.max(
      ZERO_IN_BIG_NUMBER,
      positionMargin.minus(releasedMargin)
    )

    if (remainingQuantity.isZero()) {
      return {
        margin: ZERO_IN_BIG_NUMBER,
        quantity: ZERO_IN_BIG_NUMBER,
        entryPrice: ZERO_IN_BIG_NUMBER,
        direction: currentPosition.direction
      }
    }

    return {
      margin: remainingMargin,
      entryPrice: positionEntry,
      quantity: remainingQuantity,
      direction: currentPosition.direction
    }
  }

  const remainderQuantity = order.quantity.minus(positionQuantity)
  const remainderMargin = order.quantity.isZero()
    ? ZERO_IN_BIG_NUMBER
    : order.margin.times(remainderQuantity).div(order.quantity)

  return {
    margin: remainderMargin,
    entryPrice: order.price,
    quantity: remainderQuantity,
    direction: order.isBuy ? TradeDirection.Long : TradeDirection.Short
  }
}

// strictly reducing == an opposite-side trade that closes part (or all) of an
// existing position without exceeding its size. mirrors the chain's
// `isStrictlyReducing` in applySyntheticTrade — these trades are gated on the
// maintenance margin ratio rather than the initial one.
export function isStrictlyReducingTrade({
  isBuy,
  quantity,
  currentPosition
}: {
  isBuy: boolean
  quantity: BigNumber
  currentPosition?: TcDerivativePosition
}): boolean {
  const positionQuantity = toBigNumber(currentPosition?.quantity || 0)

  if (!currentPosition || positionQuantity.isZero() || quantity.isZero()) {
    return false
  }

  if (quantity.gt(positionQuantity)) {
    return false
  }

  const isPositionLong = currentPosition.direction === TradeDirection.Long

  return isBuy !== isPositionLong
}

// MM relaxation only applies to synthetic trades (RFQ market)
export function getRequiredMarginRatio({
  market,
  isLimitOrder,
  isStrictlyReducing
}: {
  isLimitOrder: boolean
  isStrictlyReducing: boolean
  market: { initialMarginRatio: string; maintenanceMarginRatio: string }
}): BigNumber {
  return toBigNumber(
    !isLimitOrder && isStrictlyReducing
      ? market.maintenanceMarginRatio
      : market.initialMarginRatio
  )
}
